import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import { useLocation } from "react-router-dom";

import {
  isIBKR,
  getBrokerIcon,
  openNewTab,
  parseErrorMessage,
} from "utils/index";

import { toast } from "Layout/slice";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import { trackEvent } from "utils/tracking";
import { IconLeftArrow } from "assets/icons";
import { getBrokerConnectionLink } from "features/Dashboard/service";
import { ConfirmBrokerSelectionContainer } from "./SnaptradeModal.styles";
import {
  SIGNUP_CONNECT_BROKER_ACCOUNT,
  SIGNUP_CONNECTED_BROKER_ACCOUNT,
} from "features/Auth/routes";
import { IBKR_DOCS } from "utils/constants";

const ConfirmBrokerSelection = ({
  show,
  onHide,
  onBack,
  broker,
  onSuccess,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      const redirectionUrl =
        pathname === SIGNUP_CONNECT_BROKER_ACCOUNT
          ? SIGNUP_CONNECTED_BROKER_ACCOUNT
          : pathname;
      const params = {
        broker: broker.slug,
        redirect_url: redirectionUrl,
      };
      const data = await getBrokerConnectionLink(params);
      if (data.redirect_url) {
        openNewTab(data.redirect_url, "_self");
      } else if (data.success) {
        dispatch(
          toast({
            type: "info",
            body: "Surmount Paper account has been successfully connected!",
          })
        );
        onSuccess();
      }
      trackEvent({
        name: "connect_portfolio",
        label: "Porfolio",
      });
      onHide();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSurmountPaper = broker.display_name === "Surmount Paper";

  return (
    <ConfirmBrokerSelectionContainer heading="" show={show} onHide={onHide}>
      {onBack && (
        <IconLeftArrow
          onClick={onBack}
          className="icon-back"
          stroke={theme.global.color.bodyColor}
        />
      )}

      {broker?.square_logo_url && (
        <div className="text-center">
          <img
            src={getBrokerIcon(broker.square_logo_url, theme.mode)}
            className="icon-broker"
          />
        </div>
      )}
      {isSurmountPaper ? (
        <h2 className="heading">Create a new {broker.display_name} account</h2>
      ) : (
        <h2 className="heading">
          Complete the Authentication Process with {broker.display_name}
        </h2>
      )}

      <div className="content-wrapper">
        {isSurmountPaper ? (
          <ul>
            <li>
              {`You'll`} get a brand new {broker.display_name} account with
              $100,000 to start
            </li>
            <li>
              If {`You'd`} like to reset an existing paper account, please
              contact support
            </li>
          </ul>
        ) : (
          <>
            <ul>
              <li>Redirecting you to another site for identity confirmation</li>
              <li>{`You'll`} be directed back to Surmount</li>
              {isIBKR(broker) && (
                <li>
                  Read instructions for setting up the IBKR{" "}
                  <a target="_blank" rel="noreferrer" href={IBKR_DOCS}>
                    here
                  </a>
                </li>
              )}
            </ul>

            {isIBKR(broker) && (
              <b>
                NOTICE: Your IBKR connection will not appear on the dashboard
                for 24-48 hours after setting it up. Please feel free to reach
                out to customer support if your connection does not appear in 48
                hours.
              </b>
            )}
          </>
        )}

        <Button className="btn-continue" onClick={handleContinue}>
          {isLoading && <Loader className="on-btn" />} Continue
        </Button>
      </div>
    </ConfirmBrokerSelectionContainer>
  );
};

ConfirmBrokerSelection.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onBack: PropTypes.func,
  broker: PropTypes.object,
  onSuccess: PropTypes.func,
};

ConfirmBrokerSelection.displayName = "ConfirmBrokerSelection";

export default ConfirmBrokerSelection;
